"use client";

import { useEffect, useRef } from "react";
import { initRum, type RumConfig } from "./rum";
import type { ObservabilityTags } from "./tags";

export type { RumConfig };
export {
  getRumClient,
  initRum,
  isRumInitialized,
} from "./rum";
export { wrappedFetch } from "./browser-fetch";
export {
  ObservabilityErrorBoundary,
  type ObservabilityErrorBoundaryProps,
} from "./error-boundary";

export interface ObservabilityProviderProps extends ObservabilityTags {
  /**
   * Override RUM-specific config (sample rate, telemetries, identity pool, etc).
   * `appVersion` and `region` are always derived from the standard tags
   * and cannot be overridden here.
   */
  rum?: Omit<RumConfig, "appVersion" | "region">;
}

/**
 * Initialize browser-side observability. Wires the standard tag schema
 * (`app`, `env`, `version`, `service`, `region`) into CloudWatch RUM:
 *
 * - `appVersion` ← `version` — used for source-map symbolication and
 *   version-regression diagnosis in RUM error events.
 * - `region` ← `region` — RUM endpoint region.
 *
 * Drop into your root layout. Renders nothing.
 *
 * @example
 * ```tsx
 * // app/layout.tsx
 * import { ObservabilityProvider } from "@juiceco-io/observability-sdk/react";
 *
 * export default function RootLayout({ children }: { children: React.ReactNode }) {
 *   return (
 *     <html>
 *       <body>
 *         <ObservabilityProvider
 *           app="ahi"
 *           env={process.env.NEXT_PUBLIC_ENV!}
 *           version={process.env.NEXT_PUBLIC_APP_VERSION!}
 *           service="web"
 *         />
 *         {children}
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 */
export function ObservabilityProvider({
  rum,
  ...tags
}: ObservabilityProviderProps) {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    void initRum({
      ...rum,
      appVersion: tags.version,
      region: tags.region,
    });
    // tags + rum are stable for the lifetime of the page; intentionally no deps
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
